import type { AgentActionRecord } from "./agents";
import { scheduleWorkerBatch, validateWorkerPlan, type WorkerTaskPlan } from "./orchestrator";

export interface WorkerResult {
  summary: string;
  changedFieldCount?: number;
  targetFileIds?: string[];
}

export interface IsolatedWorkerContext {
  runId: string;
  taskId: string;
  role: WorkerTaskPlan["workerRole"];
  assignedFileIds: string[];
}

export type WorkerHandler = (task: WorkerTaskPlan, context: IsolatedWorkerContext) => Promise<WorkerResult>;

/** Executes a validated helper DAG. Each worker receives only assigned ids. */
export async function executeWorkerPlan(tasks: readonly WorkerTaskPlan[], handler: WorkerHandler, options: { maxConcurrency?: number; now?: () => string; initialActions?: readonly AgentActionRecord[] } = {}): Promise<AgentActionRecord[]> {
  const maxConcurrency = Math.max(1, Math.min(8, Math.trunc(options.maxConcurrency ?? 8)));
  const validation = validateWorkerPlan(tasks, maxConcurrency);
  if (!validation.ok) throw new Error(`Invalid worker plan: ${validation.errors.join("; ")}`);
  const now = options.now ?? (() => new Date().toISOString());
  const taskById = new Map(tasks.map((task) => [task.id, task]));
  const seenInitialIds = new Set<string>();
  const actions: AgentActionRecord[] = structuredClone([...(options.initialActions ?? [])]);
  for (const action of actions) {
    const task = taskById.get(action.id);
    if (!task || task.runId !== action.runId) throw new Error(`Invalid resumed worker action: ${action.id}`);
    if (seenInitialIds.has(action.id)) throw new Error(`Duplicate resumed worker action: ${action.id}`);
    if (action.status === "planned" || action.status === "running") throw new Error(`Resumed worker action must be terminal: ${action.id}`);
    seenInitialIds.add(action.id);
  }
  while (true) {
    const batch = scheduleWorkerBatch(tasks, actions, maxConcurrency);
    for (const blocked of batch.blocked) if (!actions.some((action) => action.id === blocked.task.id)) actions.push({ id: blocked.task.id, runId: blocked.task.runId, agentRole: "helper", workerRole: blocked.task.workerRole, title: blocked.task.title, status: "cancelled", targetFileIds: [...blocked.task.targetFileIds], changedFieldCount: 0, error: blocked.reason, startedAt: now(), completedAt: now() });
    if (!batch.ready.length) {
      const pending = tasks.filter((task) => !actions.some((action) => action.id === task.id));
      if (!pending.length) return actions;
      if (!actions.some((action) => action.status === "running")) throw new Error(`Worker plan stalled: ${pending.map((task) => task.id).join(", ")}`);
      continue;
    }
    await Promise.all(batch.ready.map(async task => {
      const startedAt = now();
      const action: AgentActionRecord = { id: task.id, runId: task.runId, agentRole: "helper", workerRole: task.workerRole, title: task.title, status: "running", targetFileIds: [...task.targetFileIds], changedFieldCount: 0, startedAt };
      actions.push(action);
      try {
        const result = await handler(task, { runId: task.runId, taskId: task.id, role: task.workerRole, assignedFileIds: [...task.targetFileIds] });
        action.status = "completed"; action.summary = result.summary; action.changedFieldCount = Math.max(0, Math.trunc(result.changedFieldCount ?? 0));
        if (result.targetFileIds) action.targetFileIds = [...result.targetFileIds];
      } catch (error) {
        action.status = "failed"; action.error = String(error instanceof Error ? error.message : error);
      }
      action.completedAt = now();
    }));
  }
}
